/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * ImageWrapper component using @atlaskit/css
 */
import React from 'react';

import { token } from '@atlaskit/tokens';
import { cssMap, jsx } from '@atlaskit/css';

const COVER_IMAGE_FIXED_HEIGHT = 147;
const SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH = 200;
const COVER_IMAGE_WIDTH_TO_HEIGHT_RATIO = 38 / 7;
const CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE = `round(down, calc(100cqw / (${COVER_IMAGE_WIDTH_TO_HEIGHT_RATIO})), 1px)`;

const imageWrapperStyles = cssMap({
	base: {
		width: '100%',
		objectFit: 'cover',
		position: 'absolute',
		'&:hover': {
			opacity: 1,
		},
		aspectRatio: '38 / 7',
	},
	fixed: { height: `${COVER_IMAGE_FIXED_HEIGHT}px` },
	fixedHeight: { height: `${SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH}px` },
	scaled: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- CSS calc expression for responsive height
		height: CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE as `${number}px`,
	},
	roundedCorners: { borderRadius: token('radius.xlarge') },
});

type ImageWrapperCssProps = {
	coverPicturePosition?: number | null;
	isFixedWidthImageOption?: boolean;
	smartSpaceOverviewHeight?: number;
	isRoundedCorners?: boolean;
} & Omit<React.ComponentProps<'img'>, 'css'>;

export const ImageWrapperCss = ({
	coverPicturePosition,
	isFixedWidthImageOption,
	smartSpaceOverviewHeight,
	isRoundedCorners,
	style,
	...imgProps
}: ImageWrapperCssProps): JSX.Element => {
	'use no memo'; // Exclude from React compiler - breaks unit tests when component uses compiled CSS
	return (
		<img
			{...imgProps}
			// need this empty alt tag for decorative images
			alt=""
			css={[
				imageWrapperStyles.base,
				imageWrapperStyles[
					smartSpaceOverviewHeight ? 'fixedHeight' : isFixedWidthImageOption ? 'fixed' : 'scaled'
				],
				isRoundedCorners && imageWrapperStyles.roundedCorners,
			]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- objectPosition depends on dynamic runtime value
			style={{ objectPosition: `center ${coverPicturePosition}%`, ...style }}
		/>
	);
};
